/**
 * STAIR BUILDER — the CURVED family (docs/bim-plan/33-stair-builder.md: curved, sculpted, run into an
 * open spiral; Matt 2026-10-10: "sinuous… a plaster knee wall rail that follows and a warping
 * twisting underside surface").
 *
 * A swept stair follows a centreline built of pieces, listed BOTTOM → TOP:
 *   line  — straight, `lenFt` long
 *   arc   — turning `dir` (as the walker going up turns) through `sweepDeg`, its radius easing from
 *           `r0` (bottom) to `r1` (top) — a tightening or relaxing curve
 * The top stays put: the centreline is laid out DOWNHILL from the top step, and the bottom piece
 * stretches or shrinks (a line longer or shorter, an arc through more or less sweep) to carry
 * exactly the stair's treads.
 *
 * Treads: each tread line is square to the centreline, and the spacing is chosen so the tread is
 * the stair's own tread depth at the WALK LINE — 12" in from whichever side is the narrow one
 * there (it switches sides through an S). So every tread walks the same, and the code's winder rule
 * (CRC R311.7.5.2.1: 10" at the walk line, 6" at the narrow end) is met wherever the curve allows;
 * the narrowest point and the shallowest walk-line depth are reported for the code check.
 */
import type { Pt2 } from './types'
import { stairRun } from './stair'
import type { StairLayout, StairLayoutOpts } from './stairLayout'

export type StairSweepPiece =
  | { kind: 'line'; lenFt: number }
  | { kind: 'arc'; dir: 'left' | 'right'; sweepDeg: number; r0: number; r1?: number | null }

const IN = 1 / 12
const STEP = 0.5 * IN          // the centreline is sampled every half inch

interface Sample { p: Pt2; t: Pt2; k: number; piece: number }   // point, downhill tangent, curvature (+ = turning left going DOWN)

/** sample the centreline downhill from the top step, the bottom piece extended `extraFt` past its own end */
function sampleDown(pieces: StairSweepPiece[], anchor: Pt2, upDir: Pt2, lenFt: number): Sample[] {
  const out: Sample[] = []
  let p: Pt2 = [anchor[0], anchor[1]]
  let t: Pt2 = [-upDir[0], -upDir[1]]
  // walk the pieces top → bottom; going down, a piece that turns left going up turns right
  const down = [...pieces].reverse()
  let s = 0
  let piece = pieces.length - 1
  const push = (k: number) => out.push({ p: [p[0], p[1]], t: [t[0], t[1]], k, piece })
  for (let i = 0; i < down.length && s < lenFt; i++) {
    const pc = down[i]
    piece = pieces.length - 1 - i
    const last = i === down.length - 1
    const kOf = (u: number): number => {
      if (pc.kind === 'line') return 0
      const rTop = pc.r1 ?? pc.r0, rBot = pc.r0
      const r = rTop + (rBot - rTop) * u
      return (pc.dir === 'left' ? -1 : 1) / Math.max(r, 0.5)
    }
    // the piece's length along the centreline (an arc's: the integral of r over its sweep)
    let pieceLen = pc.kind === 'line' ? pc.lenFt : (((pc.r0 + (pc.r1 ?? pc.r0)) / 2) * pc.sweepDeg * Math.PI) / 180
    if (last) pieceLen = Infinity
    let u = 0
    const arcLen = pc.kind === 'arc' ? (((pc.r0 + (pc.r1 ?? pc.r0)) / 2) * pc.sweepDeg * Math.PI) / 180 : 1
    while (u * (pc.kind === 'arc' ? arcLen : 1) < pieceLen && s < lenFt) {
      const k = kOf(Math.min(1, pc.kind === 'arc' ? u : 0))
      push(k)
      // advance one step, turning by k·STEP
      const a = k * STEP
      const c = Math.cos(a), sn = Math.sin(a)
      const mid: Pt2 = [t[0] * Math.cos(a / 2) - t[1] * Math.sin(a / 2), t[0] * Math.sin(a / 2) + t[1] * Math.cos(a / 2)]
      p = [p[0] + mid[0] * STEP, p[1] + mid[1] * STEP]
      t = [t[0] * c - t[1] * sn, t[0] * sn + t[1] * c]
      s += STEP
      u += STEP / (pc.kind === 'arc' ? arcLen : 1)
      if (pc.kind === 'line' && u >= pc.lenFt && !last) break
    }
  }
  push(out.length ? out[out.length - 1].k : 0)
  return out
}

export interface StairSweepInfo {
  /** the centreline, BOTTOM → TOP, with each sample's distance from the bottom tread line */
  pts: Pt2[]
  s: number[]
  /** each riser line's distance up the centreline, bottom riser first */
  riserS: number[]
  /** the step positions (0 = the bottom tread) that are landings: added so no flight climbs more
   *  than 12'-7" (Matt: "add it automatically to keep us in compliance") */
  landingK: number[]
  /** each arc piece within the stair: its recipe index, its middle on the centreline, the way away
   *  from its centre (a grip drags the radius along it) */
  arcs: { i: number; mid: Pt2; out: Pt2 }[]
  /** the shallowest tread at a walk line, the narrowest tread end (feet) */
  walkMinFt: number
  narrowFt: number
}

/**
 * Lay out a swept stair. `anchor` = the top step's centre, `dir` = the top's climbing direction.
 */
export function stairSweepLayout(pieces: StairSweepPiece[], anchor: Pt2, dir: Pt2, opts: StairLayoutOpts, asBuilt = false): (StairLayout & { sweep: StairSweepInfo }) | null {
  if (!pieces.length) return null
  const calc = stairRun(opts.floorToFloorFt, opts.maxRiserFt, opts.treadDepthFt)
  const r = calc.riserFt, td = opts.treadDepthFt, w = opts.widthFt, hw = w / 2
  const dl = Math.hypot(dir[0], dir[1]) || 1
  const up: Pt2 = [dir[0] / dl, dir[1] / dl]
  const walkOff = Math.max(0, hw - 1)          // the walk line, 12" in from an edge
  // generous length: every tread could be up to 3× deep at the centre on a tight curve
  const S = sampleDown(pieces, anchor, up, Math.max(4, calc.treads * td * 3 + 2))
  if (S.length < 2) return null
  // landings: as few as keep every flight within 12'-7", spread evenly up the stair
  const maxPer = Math.max(1, Math.floor(151 * IN / r + 1e-9))
  const nLand = asBuilt ? 0 : Math.max(0, Math.ceil(calc.risers / maxPer) - 1)
  const landingK: number[] = []
  for (let j = 1; j <= nLand; j++) landingK.push(Math.round((j * calc.risers) / (nLand + 1)) - 1)
  const landDepth = Math.max(w, 36 * IN)
  // march tread lines downhill: spacing so the narrow side's walk line gets exactly td
  const cuts: number[] = [0]                   // sample indices of each tread line (top first)
  let walkMin = Infinity, narrow = Infinity
  let i = 0
  for (let n = 0; n < calc.treads; n++) {
    if (landingK.includes(calc.treads - 1 - n)) {
      // a landing: as deep as the stair is wide, along its centreline
      i = Math.min(S.length - 1, i + Math.max(1, Math.round(landDepth / STEP)))
      cuts.push(i)
      continue
    }
    // average |curvature| over the next tread, iterated once
    let span = td
    for (let it = 0; it < 3; it++) {
      const j1 = Math.min(S.length - 1, i + Math.max(1, Math.round(span / STEP)))
      let kAvg = 0
      for (let j = i; j < j1; j++) kAvg += S[j].k
      kAvg /= Math.max(1, j1 - i)
      const kk = Math.min(Math.abs(kAvg), 1 / (hw + 0.05))
      span = td / Math.max(0.2, 1 - kk * walkOff)
      const narrowD = span * (1 - kk * hw), walkD = span * (1 - kk * walkOff)
      if (it === 2) { narrow = Math.min(narrow, narrowD); walkMin = Math.min(walkMin, walkD) }
    }
    i = Math.min(S.length - 1, i + Math.max(1, Math.round(span / STEP)))
    cuts.push(i)
  }
  // tread lines (top first) → bottom-first lists
  const line = (j: number): [Pt2, Pt2] => {
    const sm = S[j], n: Pt2 = [-sm.t[1], sm.t[0]]   // left of the DOWNHILL tangent = right going up
    return [[sm.p[0] - n[0] * hw, sm.p[1] - n[1] * hw], [sm.p[0] + n[0] * hw, sm.p[1] + n[1] * hw]]
  }
  const linesUp = cuts.map(line).reverse()           // index 0 = the bottom riser
  const N = calc.treads
  const treads: StairLayout['treads'] = []
  const landings: StairLayout['landings'] = []
  const riserLines: StairLayout['riserLines'] = []
  for (let k = 0; k <= N; k++) {
    const [L, R] = linesUp[k]                         // going up: [left, right]
    riserLines.push({ pts: [R, L], z: k * r, zTop: (k + 1) * r })
    if (k < N) {
      const [L2, R2] = linesUp[k + 1]
      if (landingK.includes(k)) landings.push({ poly: [R, R2, L2, L], zTop: (k + 1) * r, depth: landDepth, minDepth: landDepth })
      else treads.push({ i: k, quad: [R, R2, L2, L], zTop: (k + 1) * r })
    }
  }
  const left = linesUp.map((x) => x[0]), right = linesUp.map((x) => x[1])
  const outline = [...left, ...[...right].reverse()]
  // the centreline bottom → top, with s from the bottom tread line
  const jBot = cuts[cuts.length - 1]
  const pts: Pt2[] = [], s: number[] = []
  for (let j = jBot; j >= 0; j--) { pts.push(S[j].p); s.push((jBot - j) * STEP) }
  // sides, climbing with the treads (for the plan's cut and the 3D edges)
  const sideLines: StairLayout['sideLines'] = []
  for (let k = 0; k < N; k++) {
    sideLines.push({ pts: [left[k], left[k + 1]], z0: k * r, z1: (k + 1) * r })
    sideLines.push({ pts: [right[k], right[k + 1]], z0: k * r, z1: (k + 1) * r })
  }
  // the walk line through the stair's middle, every tread line's centre
  const usedPts = linesUp.map(([a, b]) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] as Pt2)
  return {
    calc, legs: [], treads, landings, riserLines, sideLines,
    outline, edgeChains: [left, right], usedPts, complete: true,
    sweep: { pts, s, landingK, arcs: sweepArcs(S.slice(0, jBot + 1), pieces), riserS: [...cuts].reverse().map((j) => (jBot - j) * STEP), walkMinFt: walkMin, narrowFt: narrow },
  }
}

/** the middle of each arc piece's stretch of the stair, and the way out from its centre */
function sweepArcs(S: Sample[], pieces: StairSweepPiece[]): { i: number; mid: Pt2; out: Pt2 }[] {
  const out: { i: number; mid: Pt2; out: Pt2 }[] = []
  pieces.forEach((pc, i) => {
    if (pc.kind !== 'arc') return
    const idx = S.map((x, j) => (x.piece === i ? j : -1)).filter((j) => j >= 0)
    if (!idx.length) return
    const m = S[idx[Math.floor(idx.length / 2)]]
    // left of the downhill tangent; the centre lies on the side the stair turns toward
    const n: Pt2 = [-m.t[1], m.t[0]]
    const sg = m.k > 0 ? -1 : 1
    out.push({ i, mid: m.p, out: [n[0] * sg, n[1] * sg] })
  })
  return out
}
